import { useState, useMemo } from "react";
import { View, Text, TouchableOpacity, Platform, Modal, Pressable } from "react-native";
import Svg, { Line, Circle, Polyline, Rect, Text as SvgText, G, Polygon } from "react-native-svg";
import { Ionicons } from "@expo/vector-icons";
import type { Run } from "../app/lib/types";

interface SpeedChartProps {
  runs: Run[];
}

type Mode = "mph" | "time";

export function SpeedChart({ runs }: SpeedChartProps) {
  const [containerWidth, setContainerWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>("mph");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [yearPickerOpen, setYearPickerOpen] = useState(false);

  // Sort runs by date ascending, then run number within same date
  const allSorted = useMemo(() => [...runs]
    .map((r) => ({
      date: new Date(r.date),
      speed: parseFloat(r.speedMph as any),
      time: parseFloat(r.timeSeconds as any),
      runNumber: r.runNumber || 1,
      label: `${new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    }))
    .filter((r) => !isNaN(r.speed) && r.speed > 0 && !isNaN(r.time) && r.time > 0)
    .sort((a, b) => a.date.getTime() - b.date.getTime() || a.runNumber - b.runNumber)
  , [runs]);

  // Unique years present in the data — desc order. Only show year picker
  // if the dog has runs across multiple years.
  const years = useMemo(() => {
    const set = new Set<number>();
    for (const r of allSorted) set.add(r.date.getFullYear());
    return [...set].sort((a, b) => b - a);
  }, [allSorted]);
  const showYearPicker = years.length > 1;

  const sorted = useMemo(() => {
    if (yearFilter === "all") return allSorted;
    const y = parseInt(yearFilter, 10);
    return allSorted.filter((r) => r.date.getFullYear() === y);
  }, [allSorted, yearFilter]);

  if (allSorted.length < 2) return null;

  // Chart dimensions — fill container width
  const chartWidth = containerWidth || 320;
  const chartHeight = 200;
  const paddingLeft = 45;
  const paddingRight = 16;
  const paddingTop = 28;
  const paddingBottom = 40;
  const plotWidth = chartWidth - paddingLeft - paddingRight;
  const plotHeight = chartHeight - paddingTop - paddingBottom;

  // Y axis range — pad by 10%
  // For mph: higher = better, plot bigger values higher on chart
  // For time: lower = better, INVERT axis so smaller times are higher on chart
  //   (this keeps the line shape identical — peaks are good runs in both views)
  const values = sorted.map((r) => (mode === "mph" ? r.speed : r.time));
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const range = rawMax - rawMin || (mode === "mph" ? 2 : 0.5);
  const yMin = mode === "mph" ? Math.floor(rawMin - range * 0.1) : +(rawMin - range * 0.1).toFixed(2);
  const yMax = mode === "mph" ? Math.ceil(rawMax + range * 0.1) : +(rawMax + range * 0.1).toFixed(2);
  const yRange = yMax - yMin || 1;

  // Map data to SVG coordinates
  const points = sorted.map((r, i) => {
    const v = mode === "mph" ? r.speed : r.time;
    // For time mode, invert: smaller time goes to TOP. We do this by mapping (yMax - v) instead of (v - yMin).
    const normalized = mode === "mph" ? (v - yMin) / yRange : (yMax - v) / yRange;
    return {
      x: paddingLeft + (sorted.length === 1 ? plotWidth / 2 : (i / (sorted.length - 1)) * plotWidth),
      y: paddingTop + plotHeight - normalized * plotHeight,
      label: r.label,
      speed: r.speed,
      time: r.time,
    };
  });

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(" ");

  // Y axis gridlines — labels show smaller-better (time) inverted so top label is the FASTEST
  const yGridCount = 4;
  const yGridLines = Array.from({ length: yGridCount + 1 }, (_, i) => {
    const val = yMin + (yRange / yGridCount) * i;
    // Position grids based on natural value-to-y mapping (same for both modes — it's just lines)
    const y = paddingTop + plotHeight - (i / yGridCount) * plotHeight;
    // Label shown should reflect the data: for mph, val grows upward; for time, value should shrink upward
    const labelVal = mode === "mph" ? val : yMax - (val - yMin);
    return { val: labelVal, y };
  });

  // X axis labels — show up to 5 evenly spaced
  const maxLabels = containerWidth > 350 ? 4 : 3;
  const labelStep = Math.max(1, Math.floor(sorted.length / maxLabels));
  const xLabels = points.filter((_, i) => i % labelStep === 0 || i === points.length - 1);

  // Hit area width per point
  // Map an x-pixel (relative to the chart) to the nearest data point. Drives the
  // touch/mouse overlay below so the tooltip works on mobile web (touch) too —
  // SVG <Rect onPress> hit areas don't fire reliably on touch.
  const pickAtX = (x: number) => {
    if (points.length === 0) return;
    if (points.length === 1) return setActiveIndex(0);
    const t = (x - paddingLeft) / plotWidth;
    const idx = Math.round(t * (points.length - 1));
    setActiveIndex(Math.max(0, Math.min(points.length - 1, idx)));
  };

  return (
    <View
      style={{
        backgroundColor: "#ffffff",
        borderRadius: 14,
        padding: 20,
        borderWidth: 1,
        borderColor: "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
        marginBottom: 16,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: showYearPicker ? 10 : 14, gap: 10 }}>
        <Text style={{ fontSize: 16, fontWeight: "700", color: "#374151", flexShrink: 1 }} numberOfLines={1}>
          {mode === "mph" ? "MPH" : "Run Times"}
        </Text>
        {/* MPH / Sec toggle pill */}
        <View style={{ flexDirection: "row", backgroundColor: "#f3f4f6", borderRadius: 8, padding: 2 }}>
          {(["mph", "time"] as const).map((m) => {
            const selected = mode === m;
            return (
              <TouchableOpacity
                key={m}
                onPress={() => setMode(m)}
                style={{
                  paddingVertical: 5,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  backgroundColor: selected ? "#1B2A4A" : "transparent",
                }}
              >
                <Text style={{ fontSize: 11, fontWeight: "800", color: selected ? "#ffffff" : "#6b7280", letterSpacing: 0.4 }}>
                  {m === "mph" ? "MPH" : "SEC"}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {/* Year dropdown — only when dog has runs in multiple years. Sits below the title row so it has room on small screens. */}
      {showYearPicker && (
        <View style={{ alignSelf: "flex-start", marginBottom: 14 }}>
          <TouchableOpacity
            onPress={() => setYearPickerOpen(true)}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
              backgroundColor: "#f3f4f6",
              borderRadius: 8,
              paddingVertical: 5,
              paddingHorizontal: 10,
            }}
          >
            <Text style={{ fontSize: 11, fontWeight: "800", color: "#374151", letterSpacing: 0.3 }}>
              {yearFilter === "all" ? "ALL YEARS" : yearFilter}
            </Text>
            <Ionicons name="chevron-down" size={12} color="#6b7280" />
          </TouchableOpacity>
        </View>
      )}

      {sorted.length < 2 && (
        <Text style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, paddingVertical: 30 }}>
          Not enough runs in this year to chart.
        </Text>
      )}
      <View
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
        style={{ width: "100%" }}
      >
        {containerWidth > 0 && sorted.length >= 2 && (
          <Svg
            width={chartWidth}
            height={chartHeight}
            onPress={() => setActiveIndex(null)}
          >
            {/* Y axis gridlines and labels */}
            {yGridLines.map((g, i) => (
              <Line
                key={`grid-${i}`}
                x1={paddingLeft}
                y1={g.y}
                x2={chartWidth - paddingRight}
                y2={g.y}
                stroke="#f3f4f6"
                strokeWidth={1}
              />
            ))}
            {yGridLines.map((g, i) => (
              <SvgText
                key={`ylabel-${i}`}
                x={paddingLeft - 6}
                y={g.y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#9ca3af"
              >
                {mode === "mph" ? g.val.toFixed(1) : g.val.toFixed(2) + "s"}
              </SvgText>
            ))}

            {/* Data line */}
            <Polyline
              points={polylinePoints}
              fill="none"
              stroke="#F5A623"
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {/* Data points */}
            {points.map((p, i) => (
              <Circle
                key={`dot-${i}`}
                cx={p.x}
                cy={p.y}
                r={activeIndex === i ? 6 : 4}
                fill={activeIndex === i ? "#1B2A4A" : "#F5A623"}
                stroke="#ffffff"
                strokeWidth={2}
              />
            ))}

            {/* Tooltip */}
            {activeIndex !== null && points[activeIndex] && (() => {
              const p = points[activeIndex];
              const tooltipText = mode === "mph" ? `${p.speed.toFixed(2)} mph` : `${p.time.toFixed(2)}s`;
              const tooltipWidth = 80;
              const tooltipHeight = 24;
              // Keep tooltip within chart bounds
              let tx = p.x - tooltipWidth / 2;
              if (tx < 4) tx = 4;
              if (tx + tooltipWidth > chartWidth - 4) tx = chartWidth - tooltipWidth - 4;
              const ty = p.y - 36;

              return (
                <G>
                  <Rect
                    x={tx}
                    y={ty}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    rx={6}
                    fill="#1B2A4A"
                  />
                  <SvgText
                    x={tx + tooltipWidth / 2}
                    y={ty + 16}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight="700"
                    fill="#ffffff"
                  >
                    {tooltipText}
                  </SvgText>
                  {/* Arrow — plain triangle (no rotation transform; renders
                       consistently on mobile web, unlike a rotated <Rect>). */}
                  <Polygon
                    points={`${p.x - 5},${ty + tooltipHeight} ${p.x + 5},${ty + tooltipHeight} ${p.x},${ty + tooltipHeight + 6}`}
                    fill="#1B2A4A"
                  />
                </G>
              );
            })()}

            {/* X axis labels */}
            {xLabels.map((p, i) => (
              <SvgText
                key={`xlabel-${i}`}
                x={p.x}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize={10}
                fill="#9ca3af"
              >
                {p.label}
              </SvgText>
            ))}
          </Svg>
        )}
        {/* Touch/mouse overlay — tap or drag anywhere to move the tooltip (works on mobile web) */}
        {containerWidth > 0 && sorted.length >= 2 && (
          <View
            style={{ position: "absolute", left: 0, top: 0, width: chartWidth, height: chartHeight }}
            onStartShouldSetResponder={() => true}
            onMoveShouldSetResponder={() => true}
            onResponderGrant={(e) => pickAtX(e.nativeEvent.locationX)}
            onResponderMove={(e) => pickAtX(e.nativeEvent.locationX)}
            {...(Platform.OS === "web" ? {
              onMouseMove: (e: any) => pickAtX(e.nativeEvent.offsetX),
              onMouseLeave: () => setActiveIndex(null),
            } as any : {})}
          />
        )}
      </View>

      {/* Year picker modal */}
      <Modal visible={yearPickerOpen} transparent animationType="fade" onRequestClose={() => setYearPickerOpen(false)}>
        <Pressable
          onPress={() => setYearPickerOpen(false)}
          style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "center", alignItems: "center", padding: 24 }}
        >
          <Pressable onPress={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, backgroundColor: "#fff", borderRadius: 14, overflow: "hidden" }}>
            <Text style={{ fontSize: 12, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 }}>
              Filter by year
            </Text>
            {["all", ...years.map((y) => String(y))].map((y) => {
              const selected = yearFilter === y;
              return (
                <TouchableOpacity
                  key={y}
                  onPress={() => { setYearFilter(y); setYearPickerOpen(false); setActiveIndex(null); }}
                  style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 16, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}
                >
                  <Text style={{ fontSize: 15, fontWeight: selected ? "700" : "500", color: selected ? "#1B2A4A" : "#374151" }}>
                    {y === "all" ? "All years" : y}
                  </Text>
                  {selected && <Ionicons name="checkmark" size={20} color="#1B2A4A" />}
                </TouchableOpacity>
              );
            })}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}
